/**
 * Who can submit and see a request type, which team owns it, and which work type it maps to
 * (RESTORE_PLAN R1.3, from the pre-November RequestDialog and RequestsList).
 *
 * The live builder already had "who can submit" (everyone, employees, groups, organizations) and
 * "who can see". The old dialog had portal visibility by specific teams, a group, a department or
 * named individuals, with a team created inline. The two are merged into one "who can submit"
 * choice with seven options; the team list comes from the company's spaces.
 */

export type SubmitterAccess =
  | 'everyone'
  | 'employees_only'
  | 'specific_teams'
  | 'specific_groups'
  | 'specific_department'
  | 'specific_individuals'
  | 'specific_orgs';

export const SUBMITTER_ACCESS_OPTIONS: { value: SubmitterAccess; label: string; hint: string }[] = [
  { value: 'everyone', label: 'Everyone (open portal)', hint: 'Anyone who can reach the portal' },
  { value: 'employees_only', label: 'Employees only', hint: 'Signed-in customers of this company' },
  { value: 'specific_teams', label: 'Specific teams', hint: 'Teams from the company\'s spaces' },
  { value: 'specific_groups', label: 'Specific groups', hint: 'Named Jira or directory groups' },
  { value: 'specific_department', label: 'A department', hint: 'One named department' },
  { value: 'specific_individuals', label: 'Named individuals', hint: 'A list of people' },
  { value: 'specific_orgs', label: 'Specific organizations', hint: 'JSM organizations' },
];

/** The work types a request type can map to; the engagement's own work types are added at run time. */
export const WORK_TYPE_CATALOG: { id: string; name: string }[] = [
  { id: 'service-request-management', name: 'Service Request Management' },
  { id: 'incident-management', name: 'Incident Management' },
  { id: 'change-management', name: 'Change Management' },
  { id: 'problem-management', name: 'Problem Management' },
  { id: 'post-incident-review', name: 'Post-Incident Review' },
];

export interface RequestTypeAccess {
  submitterAccess: SubmitterAccess;
  allowedTeams?: string[];
  allowedGroups?: string[];
  allowedDepartment?: string;
  allowedIndividuals?: string[];
  allowedOrgs?: string[];
}

export interface VisibilityBadge {
  kind: 'teams' | 'groups' | 'department' | 'individuals' | 'orgs' | 'employees';
  label: string;
  values: string[];
}

/** Lines, commas and semicolons all separate names; blanks and repeats are dropped. */
export function parseNameList(input: string | string[] | undefined | null): string[] {
  const parts = Array.isArray(input) ? input : (input || '').split(/[\n,;]/);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of parts) {
    const v = (raw || '').trim();
    if (!v) continue;
    const key = v.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(v);
  }
  return out;
}

/** The problems that stop a restricted request type from being saved; the old dialog refused an empty restriction. */
export function validateAccess(a: RequestTypeAccess): string[] {
  const errors: string[] = [];
  switch (a.submitterAccess) {
    case 'specific_teams':
      if (parseNameList(a.allowedTeams).length === 0) errors.push('Choose at least one team, or add one');
      break;
    case 'specific_groups':
      if (parseNameList(a.allowedGroups).length === 0) errors.push('Name at least one group');
      break;
    case 'specific_department':
      if (!a.allowedDepartment?.trim()) errors.push('Name the department');
      break;
    case 'specific_individuals':
      if (parseNameList(a.allowedIndividuals).length === 0) errors.push('Name at least one person');
      break;
    case 'specific_orgs':
      if (parseNameList(a.allowedOrgs).length === 0) errors.push('Name at least one organization');
      break;
  }
  return errors;
}

/** The badge the forms list shows for a restricted request type; none for an open one. */
export function visibilityBadge(a: Partial<RequestTypeAccess> | null | undefined): VisibilityBadge | null {
  if (!a) return null;
  switch (a.submitterAccess) {
    case 'employees_only':
      return { kind: 'employees', label: 'Employees only', values: [] };
    case 'specific_teams':
      return { kind: 'teams', label: 'Teams', values: parseNameList(a.allowedTeams) };
    case 'specific_groups':
      return { kind: 'groups', label: 'Groups', values: parseNameList(a.allowedGroups) };
    case 'specific_department':
      return { kind: 'department', label: 'Department', values: a.allowedDepartment?.trim() ? [a.allowedDepartment.trim()] : [] };
    case 'specific_individuals':
      return { kind: 'individuals', label: 'Individuals', values: parseNameList(a.allowedIndividuals) };
    case 'specific_orgs':
      return { kind: 'orgs', label: 'Organizations', values: parseNameList(a.allowedOrgs) };
    default:
      return null;
  }
}

export interface SpaceWithTeams {
  id: string;
  name?: string;
  type?: string;
  status?: string;
  teams?: string[];
}

/** Every team named on the company's active spaces, plus any already named on its request types, once each, in order of appearance. */
export function collectTeams(spaces: SpaceWithTeams[], requestTypes: { team?: string; allowedTeams?: string[] }[] = []): string[] {
  const names: string[] = [];
  for (const s of spaces) {
    if ((s.status ?? 'active') === 'archived') continue;
    for (const t of s.teams || []) names.push(t);
  }
  for (const rt of requestTypes) {
    if (rt.team) names.push(rt.team);
    for (const t of rt.allowedTeams || []) names.push(t);
  }
  return parseNameList(names);
}

/** The space an inline-created team is written to: the first active JSM space, else the first active space. */
export function spaceForNewTeam(spaces: SpaceWithTeams[]): SpaceWithTeams | null {
  const active = spaces.filter((s) => (s.status ?? 'active') !== 'archived');
  return active.find((s) => (s.type || 'jsm') === 'jsm') || active[0] || null;
}

/** The work type a builder opened from a work-type page already belongs to, when the id names one. */
export function workTypeFromServiceProjectId(serviceProjectId: string | undefined, catalog = WORK_TYPE_CATALOG): { id: string; name: string } | null {
  if (!serviceProjectId) return null;
  const key = serviceProjectId.toLowerCase();
  return catalog.find((w) => w.id === key || key.includes(w.id)) || null;
}

/** The catalog plus the engagement's own work types (its `workTypes` record keys), once each. */
export function workTypeOptions(engagementWorkTypes: Record<string, any> | undefined | null, catalog = WORK_TYPE_CATALOG): { id: string; name: string }[] {
  const out = [...catalog];
  for (const id of Object.keys(engagementWorkTypes || {})) {
    if (out.some((w) => w.id === id)) continue;
    const name = engagementWorkTypes?.[id]?.name || id.split(/[-_]/).map((p: string) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
    out.push({ id, name });
  }
  return out;
}
